/**
 * Chequea si los items de una venta usan un precio distinto al de la lista
 * asignada al cliente. Reglas:
 *   - Sólo aplica cuando el cliente tiene `lista_precios_id` seteado.
 *   - Se compara el precio final con IVA contra `productos.precios[lista_id]`
 *     (que se guardan CON IVA en el catálogo).
 *   - Tolerancia relativa `tolerancia` (default 0.5%).
 *   - Si el precio matchea otra lista → flag `lista_ajena` con su nombre.
 *   - Si no matchea ninguna → flag `fuera_de_lista`.
 *   - Ignora líneas virtuales de promo/descuento y líneas sin producto_id.
 *
 * @param {{
 *   items: Array<{producto_id?:string, descripcion?:string, precio_unitario?:number, iva_pct?:number, kilos?:number, cantidad?:number, tipo_producto?:string}>,
 *   cliente: {id?:string, lista_precios_id?:string} | null,
 *   productos: Array<{id:string, nombre?:string, precios?:Record<string,number>, iva_pct?:number}>,
 *   listasPrecios: Array<{id:string, nombre?:string}>,
 *   tolerancia?: number,
 *   pricesNet?: boolean,  // true si items.precio_unitario está sin IVA (POS backend). false = c/IVA (FacturasPage form).
 *   ivaPctFallback?: number,
 * }} args
 * @returns {Array<{producto_id:string, descripcion:string, precio_facturado_con_iva:number, precio_lista_cliente:number|null, tipo:'lista_ajena'|'fuera_de_lista', lista_detectada_nombre?:string, precio_detectado?:number, diferencia_pct:number|null}>}
 */
export function checkListaIncorrecta({
  items,
  cliente,
  productos,
  listasPrecios,
  tolerancia = 0.005,
  pricesNet = true,
  ivaPctFallback = 10.5,
}) {
  const listaCliId = cliente?.lista_precios_id;
  if (!listaCliId) return [];
  if (!Array.isArray(items) || items.length === 0) return [];
  const prodMap = new Map((productos || []).map((p) => [p.id, p]));
  const listaMap = new Map((listasPrecios || []).map((l) => [l.id, l.nombre || l.id]));

  const flagged = [];
  for (const it of items) {
    if (it.tipo_producto === "descuento_promo") continue;
    const pid = it.producto_id;
    if (!pid) continue;
    const prod = prodMap.get(pid);
    if (!prod) continue;
    const precios = prod.precios || {};
    const pu = parseFloat(it.precio_unitario || 0);
    if (!(pu > 0)) continue;
    const ivaPct = it.iva_pct != null
      ? parseFloat(it.iva_pct)
      : (prod.iva_pct != null ? parseFloat(prod.iva_pct) : ivaPctFallback);
    const precioCIva = pricesNet ? pu * (1 + ivaPct / 100) : pu;

    const precioEsperado = parseFloat(precios[listaCliId] || 0);
    if (precioEsperado > 0 && Math.abs(precioCIva - precioEsperado) / precioEsperado <= tolerancia) {
      continue; // OK, matchea la lista del cliente
    }
    // Buscar match en otra lista
    let listaDetId = null, precioDetectado = null;
    for (const [lid, pval] of Object.entries(precios)) {
      const pv = parseFloat(pval || 0);
      if (!(pv > 0)) continue;
      if (lid === listaCliId) continue;
      if (Math.abs(precioCIva - pv) / pv <= tolerancia) {
        listaDetId = lid;
        precioDetectado = pv;
        break;
      }
    }
    const diffPct = precioEsperado > 0
      ? Math.round(((precioCIva - precioEsperado) / precioEsperado) * 10000) / 100
      : null;
    flagged.push({
      producto_id: pid,
      descripcion: (it.descripcion || prod.nombre || "").toString(),
      kilos: parseFloat(it.kilos || it.cantidad || 0),
      precio_facturado_con_iva: Math.round(precioCIva * 100) / 100,
      precio_lista_cliente: precioEsperado > 0 ? Math.round(precioEsperado * 100) / 100 : null,
      tipo: listaDetId ? "lista_ajena" : "fuera_de_lista",
      lista_detectada_id: listaDetId,
      lista_detectada_nombre: listaDetId ? (listaMap.get(listaDetId) || listaDetId) : null,
      precio_detectado: precioDetectado ? Math.round(precioDetectado * 100) / 100 : null,
      diferencia_pct: diffPct,
    });
  }
  return flagged;
}
